import React from 'react';
import { 
  TalentPageCard, 
  TalentPageCardContent, 
  TalentPageCardHeader, 
  TalentPageCardTitle 
} from '../ui/TalentPageCard';
import { Badge } from '../ui/Badge';
import { 
  ChartBarIcon, 
  TrophyIcon, 
  ExclamationTriangleIcon,
  LightBulbIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { CalibrationInsights } from '../../types';

interface CalibrationInsightsCardProps {
  calibrationInsights: CalibrationInsights;
  className?: string;
}

const CalibrationInsightsCard: React.FC<CalibrationInsightsCardProps> = ({
  calibrationInsights,
  className = ''
}) => {
  // Helper function to format confidence as percentage
  const formatConfidence = (confidence: number) => {
    return `${Math.round(confidence * 100)}%`;
  };

  return (
    <TalentPageCard className={`${className}`} variant="default">
      <TalentPageCardHeader className="p-6 pb-4">
        <div className="flex items-center justify-between">
          <TalentPageCardTitle className="flex items-center gap-2">
            <ChartBarIcon className="h-5 w-5 text-[var(--amazon-orange)]" />
            Calibration Insights
          </TalentPageCardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[var(--font-size-xs)]">
              Confidence: {formatConfidence(calibrationInsights.confidence)}
            </Badge>
          </div>
        </div>
      </TalentPageCardHeader>

      <TalentPageCardContent className="p-6 pt-0 space-y-6">


        {/* Summary */}
        {calibrationInsights.summary && (
          <div className="bg-blue-50 border-l-4 border-[#0073BB] p-4 rounded-r-lg">
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-5 w-5 text-[#0073BB] mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-[var(--font-size-sm)] font-[var(--font-weight-semibold)] text-[#232F3E] mb-2">Executive Summary</h4>
                <p className="text-[var(--font-size-sm)] text-[#232F3E] leading-relaxed">
                  {calibrationInsights.summary}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Key Strengths */}
        {calibrationInsights.key_strengths && calibrationInsights.key_strengths.length > 0 && (
          <div>
            <h4 className="text-[var(--font-size-sm)] font-[var(--font-weight-semibold)] text-[#232F3E] mb-3 flex items-center gap-2">
              <TrophyIcon className="h-4 w-4 text-[#067D62]" />
              Key Strengths
            </h4>
            <div className="space-y-2">
              {calibrationInsights.key_strengths.map((strength, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-[#067D62]">
                  <div className="w-2 h-2 bg-[#067D62] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-[var(--font-size-sm)] text-[#232F3E] leading-relaxed">{strength}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Development Areas */}
        {calibrationInsights.development_areas && calibrationInsights.development_areas.length > 0 && (
          <div>
            <h4 className="text-[var(--font-size-sm)] font-[var(--font-weight-semibold)] text-[#232F3E] mb-3 flex items-center gap-2">
              <ExclamationTriangleIcon className="h-4 w-4 text-[#F89406]" />
              Development Areas
            </h4>
            <div className="space-y-2">
              {calibrationInsights.development_areas.map((area, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-[#F89406]">
                  <div className="w-2 h-2 bg-[#F89406] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-[var(--font-size-sm)] text-[#232F3E] leading-relaxed">{area}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {calibrationInsights.recommendations && calibrationInsights.recommendations.length > 0 && (
          <div>
            <h4 className="text-[var(--font-size-sm)] font-[var(--font-weight-semibold)] text-[#232F3E] mb-3 flex items-center gap-2">
              <LightBulbIcon className="h-4 w-4 text-[#FF9900]" />
              Recommendations
            </h4>
            <div className="space-y-2">
              {calibrationInsights.recommendations.map((recommendation, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg border border-[#FF9900]">
                  <div className="w-2 h-2 bg-[#FF9900] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-[var(--font-size-sm)] text-[#232F3E] leading-relaxed">{recommendation}</p>
                </div>
              ))}
            </div>
          </div>
        )}




      </TalentPageCardContent>
    </TalentPageCard>
  );
};

export default CalibrationInsightsCard;